import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useDropzone } from "react-dropzone";
import api from "../../utils/api";

const steps = ["Work Order Info", "Details & File", "Review & Submit"];

const AddWorkOrderSmart = ({ propertyId }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [createdBy, setCreatedBy] = useState(null);
  const [formConfig, setFormConfig] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setCreatedBy(parsedUser?.userId ?? null);
      } catch (e) {
        console.error("Failed to parse user from sessionStorage", e);
      }
    }
    // Load form config (controls labels, field visibility, notify checkbox)
    api.get("/wo-form-config").then(r => {
      if (r.data && Object.keys(r.data).length > 0) setFormConfig(r.data);
      setConfigLoaded(true);
    }).catch(() => { setConfigLoaded(true); });
  }, []);

  const [formData, setFormData] = useState(() => {
    const draft = localStorage.getItem("work_order_draft");
    const parsedDraft = draft ? JSON.parse(draft) : {};

    return {
      description: parsedDraft.description || "",
      details: parsedDraft.details || "",
      property_id: propertyId || parsedDraft.property_id || "",
      workorder_type_id: parsedDraft.workorder_type_id || "",
      workorder_subtype_id: parsedDraft.workorder_subtype_id || "",
      location_id: parsedDraft.location_id || "",
      contact_back_requested: parsedDraft.contact_back_requested || false,
    };
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    localStorage.setItem("work_order_draft", JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    const fetchTypes = async () => {
      if (!propertyId) return;
      try {
        const res = await api.get(`workorder-types/internal`, {
          params: { member_code: propertyId },
        });
        setWorkOrderTypes(res.data || []);
      } catch (err) {
        console.error("Failed to fetch work order types:", err);
      }
    };
    fetchTypes();
  }, [propertyId]);

  useEffect(() => {
    if (!formData.workorder_type_id) {
      setSubtypes([]);
      setLocations([]);
      return;
    }

    const fetchSubtypes = async () => {
      try {
        const res = await api.get("/workorder-subtypes-by-type", {
          params: { type_id: formData.workorder_type_id },
        });
        setSubtypes(res.data || []);
      } catch (err) {
        console.error("Failed to fetch subtypes:", err);
        setSubtypes([]);
      }
    };

    const fetchLocations = async () => {
      try {
        const res = await api.get("/workorder-locations-by-type", {
          params: { type_id: formData.workorder_type_id },
        });
        setLocations(res.data || []);
      } catch (err) {
        console.error("Failed to fetch locations:", err);
        setLocations([]);
      }
    };

    fetchSubtypes();
    fetchLocations();

    setFormData((prev) => ({
      ...prev,
      workorder_subtype_id: "",
      location_id: "",
    }));
  }, [formData.workorder_type_id]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: {
      "image/*": [],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
      ],
    },
  });

  const nextStep = () => setActiveStep((s) => s + 1);
  const prevStep = () => setActiveStep((s) => s - 1);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payloadJSON = {
        property_id: formData.property_id,
        description: formData.description.trim(),
        details: formData.details.trim(),
        workorder_type_id: formData.workorder_type_id,
        workorder_subtype_id: formData.workorder_subtype_id || null,
        location_id: formData.location_id || null,
        created_by: createdBy,
        contact_back_requested: formData.contact_back_requested,
        notify_member: formData.contact_back_requested, // maps to backend notify_member
      };

      const createRes = await api.post("/addWorkOrder", payloadJSON);
      const workorder_id = createRes.data?.workOrderId;
      if (!workorder_id) throw new Error("Missing workOrderId in response");

      if (selectedFiles.length > 0) {
        const uploadData = new FormData();
        uploadData.append("workorder_id", workorder_id);
        uploadData.append("uploaded_by", createdBy);
        selectedFiles.forEach((file) => uploadData.append("upload_urls", file));

        await api.post("/workorder-files", uploadData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      setFormData({
        description: "",
        details: "",
        property_id: propertyId || "",
        workorder_type_id: "",
        workorder_subtype_id: "",
        location_id: "",
        contact_back_requested: false,
      });
      setSelectedFiles([]);
      setActiveStep(0);
      localStorage.removeItem("work_order_draft");

      setSnackbar({
        open: true,
        message: "✅ Work order created successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Submit error (AddWorkOrderSmart):", err);
      setSnackbar({
        open: true,
        message:
          err.response?.data?.message || err.message || "Submission failed",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const sectionCardSx = {
    ...pageCardSx,
    p: { xs: 1.5, sm: 2, md: 2.5 },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const readonlyFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f7f9fc",
      borderRadius: "10px",
    },
  };

  const selectSx = {
    borderRadius: "10px",
    backgroundColor: "rgba(255,255,255,0.96)",
    "& .MuiSelect-select": {
      py: 1.7,
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.5,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2.25,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Card sx={sectionCardSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
              Work Order Information
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Select the work order type, subtype, and location for this request.
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Tooltip title="Select the type of work order">
                    <InputLabel
                      sx={{
                        backgroundColor: "white",
                        px: 0.5,
                      }}
                    >
                      Work Order Type
                    </InputLabel>
                  </Tooltip>
                  <Select
                    name="workorder_type_id"
                    value={formData.workorder_type_id}
                    onChange={handleFormChange}
                    required
                    sx={selectSx}
                  >
                    {workOrderTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!subtypes.length}>
                  <InputLabel sx={{ backgroundColor: "white", px: 0.5 }}>
                    Subtype
                  </InputLabel>
                  <Select
                    name="workorder_subtype_id"
                    value={formData.workorder_subtype_id}
                    onChange={handleFormChange}
                    required
                    sx={selectSx}
                  >
                    {subtypes.map((sub) => (
                      <MenuItem key={sub.id} value={sub.id}>
                        {sub.subtype_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!locations.length}>
                  <InputLabel sx={{ backgroundColor: "white", px: 0.5 }}>
                    Location
                  </InputLabel>
                  <Select
                    name="location_id"
                    value={formData.location_id}
                    onChange={handleFormChange}
                    required
                    sx={selectSx}
                  >
                    {locations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.location_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Property ID"
                  name="property_id"
                  value={formData.property_id}
                  disabled
                  sx={readonlyFieldSx}
                />
              </Grid>
            </Grid>
          </Card>
        );

      case 1:
        return (
          <Card sx={sectionCardSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
              Details & Attachments
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Add a description, more details, and any supporting files.
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={formConfig?.field_labels?.description || "Description"}
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  multiline
                  rows={2}
                  required
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={formConfig?.field_labels?.details   || "Details"}
                  name="details"
                  value={formData.details}
                  onChange={handleFormChange}
                  multiline
                  rows={4}
                  required
                  sx={textFieldSx}
                />
              </Grid>

              {/* Only render after config loaded AND admin enabled the checkbox */}
              {configLoaded && formConfig?.fields?.notify_checkbox && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.contact_back_requested}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            contact_back_requested: e.target.checked,
                          }))
                        }
                        sx={{ color: "#2f63ad", "&.Mui-checked": { color: "#2f63ad" } }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: "#223142", fontWeight: 500 }}>
                        {formConfig?.fields?.notify_label || "Please contact me back regarding this request"}
                      </Typography>
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#6b778c", mb: 1 }}>
                  Attach Files
                </Typography>

                <Box
                  {...getRootProps()}
                  sx={{
                    p: 3,
                    border: "2px dashed rgba(47,99,173,0.25)",
                    borderRadius: "16px",
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: isDragActive
                      ? "rgba(47,99,173,0.08)"
                      : "rgba(47,99,173,0.02)",
                    transition: "all 0.2s ease",
                    mb: 2,
                  }}
                >
                  <input {...getInputProps()} />
                  <CloudUploadIcon sx={{ fontSize: 42, color: "#2f63ad", mb: 1 }} />
                  <Typography sx={{ fontWeight: 600, color: "#223142", mb: 0.5 }}>
                    {isDragActive
                      ? "Drop files here..."
                      : "Drag and drop files here, or click to upload"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    Images, PDF, DOC, and DOCX files are supported.
                  </Typography>
                </Box>

                {selectedFiles.length > 0 ? (
                  <Box
                    sx={{
                      border: "1px solid rgba(47,99,173,0.08)",
                      borderRadius: "14px",
                      overflow: "hidden",
                    }}
                  >
                    {selectedFiles.map((file, idx) => (
                      <Box
                        key={`${file.name}-${idx}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 1.5,
                          py: 1.1,
                          borderBottom:
                            idx !== selectedFiles.length - 1
                              ? "1px solid rgba(47,99,173,0.08)"
                              : "none",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#223142",
                            maxWidth: "80%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={file.name}
                        >
                          {file.name}
                        </Typography>

                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFile(idx)}
                          sx={{
                            border: "1px solid rgba(220,38,38,0.12)",
                            borderRadius: "10px",
                            color: "#dc2626",
                            backgroundColor: "rgba(220,38,38,0.04)",
                            "&:hover": {
                              backgroundColor: "rgba(220,38,38,0.1)",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: "#8a94a6" }}>
                    No files uploaded yet.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Card>
        );

      case 2:
        return (
          <Card sx={sectionCardSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                Review Work Order
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Confirm everything before submitting the work order.
            </Typography>

            <Divider sx={{ mb: 2.5, borderColor: "rgba(47,99,173,0.08)" }} />

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Type
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#223142" }}>
                  {workOrderTypes.find((t) => t.id === formData.workorder_type_id)?.type || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Subtype
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#223142" }}>
                  {subtypes.find((s) => s.id === formData.workorder_subtype_id)?.subtype_name || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Location
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#223142" }}>
                  {locations.find((l) => l.id === formData.location_id)?.location_name || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Property ID
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#223142" }}>
                  {formData.property_id || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Description
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 400, color: "#223142" }}>
                  {formData.description || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Details
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 400, color: "#223142", whiteSpace: "pre-wrap" }}
                >
                  {formData.details || "N/A"}
                </Typography>
              </Grid>

              {/* Only show notify row if the checkbox is enabled in form config */}
              {configLoaded && formConfig?.fields?.notify_checkbox && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    {formConfig?.fields?.notify_label || "Contact Back Requested"}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#223142" }}>
                    {formData.contact_back_requested ? "Yes" : "No"}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#6b778c", mb: 1 }}>
                  Files
                </Typography>
                {selectedFiles.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {selectedFiles.map((file, idx) => (
                      <Box
                        key={`${file.name}-${idx}`}
                        sx={{
                          px: 1.25,
                          py: 1,
                          border: "1px solid rgba(47,99,173,0.08)",
                          borderRadius: "12px",
                          backgroundColor: "rgba(47,99,173,0.03)",
                          maxWidth: 220,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "#6b778c", fontStyle: "italic" }}
                          title={file.name}
                          noWrap
                        >
                          {file.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="subtitle1" sx={{ fontWeight: 400, color: "#223142" }}>
                    None
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Card>
        );

      default:
        return null;
    }
  };

  const nextDisabled =
    isSubmitting ||
    (activeStep === 0 && !formData.workorder_type_id) ||
    (activeStep === 1 && (!formData.description.trim() || !formData.details.trim()));

  return (
    <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
      <Box
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          pt: 2.25,
          pb: 1.5,
          borderBottom: "1px solid rgba(47,99,173,0.08)",
          backgroundColor: "rgba(47,99,173,0.02)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <AssignmentIcon sx={{ color: "#2f63ad" }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
            Add Work Order
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
          Create a new work order and attach supporting files if needed.
        </Typography>

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            "& .MuiStepLabel-label": {
              fontSize: "0.9rem",
            },
            "& .MuiStepLabel-label.Mui-active": {
              fontWeight: 700,
              color: "#223142",
            },
            "& .MuiStepLabel-label.Mui-completed": {
              fontWeight: 600,
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {renderStepContent()}

        <Divider sx={{ my: 3, borderColor: "rgba(47,99,173,0.08)" }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={prevStep}
            disabled={activeStep === 0 || isSubmitting}
            sx={secondaryButtonSx}
          >
            Back
          </Button>

          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={nextStep}
              disabled={nextDisabled}
              sx={primaryButtonSx}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={primaryButtonSx}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Submit Work Order"
              )}
            </Button>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default AddWorkOrderSmart;
